import React from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { useClientsState } from "atoms/clientsAtom";
import {
  useClientTransaction,
  useGetTransactionCalculation,
} from "features/ClientInfo/api";
import { ClientDiscountForm } from "features/ClientInfo/ClientInfo";

import { MediumText } from "components/MediumText/MediumText";
import { NamedCounter } from "components/NamedCounter/NamedCounter";
import { Paragraph } from "components/Paragraph/Paragraph";
import { Button, EButtonVariant } from "components/Button/Button";
import { NamedInput } from "components/NamedInput/NamedInput";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import styles from "../../styles.module.css";

const FIELDS = [
  {
    title: "Начальный % бонуса",
    varName: "startbonus",
    symbol: "%",
    titleWidth: 140,
  },
  {
    title: "Шаг бонуса",
    varName: "shiftbonus",
    symbol: "",
    titleWidth: 130,
  },
  {
    title: "Период увеличения бонуса",
    varName: "shiftbonussum",
    symbol: "",
    titleWidth: 200,
  },
  {
    title: "Текущий % бонуса",
    varName: "bonuspercent",
    symbol: "%",
    titleWidth: 140,
  },
  {
    title: "Конечный % бонуса",
    varName: "endbonus",
    symbol: "%",
    titleWidth: 130,
  },
  {
    title: "Следующее увеличение бонуса",
    varName: "nextbonussum",
    symbol: "",
    titleWidth: 200,
  },
];

type ClientInfoBonus = {
  add: string;
  reduce: string;
  comment: string;
};

type Props = {
  clientDiscountControl: Control<ClientDiscountForm>;
};

export const Bonuses = ({ clientDiscountControl }: Props): JSX.Element => {
  const { clientsState } = useClientsState();

  const { mutate: clientTransaction } = useClientTransaction();

  const { data: transactionCalculation } = useGetTransactionCalculation(
    clientsState.activeItem?.clientid || ""
  );

  const { control, handleSubmit, reset } = useForm<ClientInfoBonus>({
    defaultValues: {
      add: "",
      reduce: "",
      comment: "",
    },
  });

  const handleActionClick = (data: ClientInfoBonus, isReduce?: boolean) => {
    clientTransaction(
      {
        total: isReduce ? +data.reduce : +data.add,
        comment: data.comment,
        clientid: clientsState.activeItem?.clientid || "",
        action: isReduce ? "reduce" : "add",
      },
      {
        onSuccess: () => reset(),
      }
    );
  };

  return (
    <>
      <div className="flex flex-wrap mt-2 gap-x-1 gap-y-4 justify-center">
        {FIELDS.map((currentField) => (
          <div key={currentField.title} className="flex ml-5 items-center">
            <Controller
              name={currentField.varName as keyof ClientDiscountForm}
              control={clientDiscountControl}
              render={({ field }) => (
                <NamedCounter
                  {...field}
                  title={currentField.title}
                  value={field.value as string}
                  symbol={currentField.symbol}
                  titleWidth={currentField.titleWidth}
                />
              )}
            />
          </div>
        ))}
      </div>
      <div className={styles.clientDataWrapper}>
        <MediumText size={16}>Внести/изъять бонусы</MediumText>
        <div>
          <Paragraph>
            Накопленные бонусы:
            <span className="text-purple-500 ml-2">
              {transactionCalculation}
            </span>
          </Paragraph>
        </div>
        <VerticalSpace size={20} />
        <div className="flex items-center">
          <div className="flex items-center mr-16">
            <Controller
              name="add"
              control={control}
              render={({ field }) => (
                <NamedInput
                  {...field}
                  inputWidth={60}
                  type="number"
                  placeholder="0"
                  label="Внести бонусы"
                />
              )}
            />
            <Button
              className="ml-4"
              variant={EButtonVariant.SECONDARY}
              onClick={handleSubmit((data) => handleActionClick(data, false))}
            >
              Начислить
            </Button>
          </div>
          <div className="flex items-center">
            <Controller
              name="reduce"
              control={control}
              render={({ field }) => (
                <NamedInput
                  {...field}
                  inputWidth={60}
                  type="number"
                  placeholder="0"
                  label="Списать бонусы"
                />
              )}
            />
            <Button
              className="ml-4"
              variant={EButtonVariant.SECONDARY}
              onClick={handleSubmit((data) => handleActionClick(data, true))}
            >
              Списать
            </Button>
          </div>
        </div>
        <VerticalSpace size={20} />
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <NamedInput
              {...field}
              label="Комментарий"
              className={styles.bonus_comment_input}
              classNameContainer={styles.bonus_comment_container}
            />
          )}
        />
      </div>
    </>
  );
};
